import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, Download, FileText, Users, UserX } from 'lucide-react';
import Layout from '../../components/layout/AppLayout';
import Header from '../../../shared/components/layout/Header';
import { useSmartBack } from '../../../shared/navigation';
import Loader from '../../../shared/components/ui/Loader';
import {
  Callout,
  EmptyState,
  FilterBar,
  PageBody,
  SegmentedControl,
  Table,
  TableCard,
  TBody,
  Td,
  Th,
  THead,
  Tile,
  TileRow,
  Tr,
  EmptyRow
} from '../../../shared/components/ui/primitives';
import { getDailyUpdateReport, getDailyUpdates } from '../../services/api/implementation/impl';
import DailyUpdateCard, { formatDay, formatTime } from './components/DailyUpdateCard';
import { groupByDay, toKey, updatesToCsv, weekStart } from './components/dailyUpdateSheet';

const RANGES = [
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This week' },
  { value: 'month', label: 'Last 30 days' },
  { value: 'custom', label: 'Custom' }
];

const VIEWS = [
  { value: 'updates', label: 'Updates' },
  { value: 'report', label: 'Report' }
];

/**
 * Daily updates — what each person got done, what is next, and what is in
 * their way.
 *
 * Posting is optional and happens on the app at punch-out; this is where they
 * are read. The server decides whose: HR and Management see the company, a
 * manager sees their reports, anyone else sees their own.
 *
 * Opened from a daily-update notification with ?date= (and ?focus= for a
 * single update), so the bell lands on the day it was about.
 */
const DailyUpdates = () => {
  const { onBack } = useSmartBack({ fallback: '/builder/workforce?tab=employees' });
  const [params] = useSearchParams();
  const linkedDate = /^\d{4}-\d{2}-\d{2}$/.test(params.get('date') || '') ? params.get('date') : null;
  const focus = Number(params.get('focus')) || null;

  const today = toKey(new Date());
  const [range, setRange] = useState(linkedDate ? 'custom' : 'today');
  const [from, setFrom] = useState(linkedDate || today);
  const [to, setTo] = useState(linkedDate || today);
  const [person, setPerson] = useState('');
  const [view, setView] = useState('updates');

  const pickRange = (value) => {
    setRange(value);
    if (value === 'today') {
      setFrom(today);
      setTo(today);
    } else if (value === 'week') {
      setFrom(weekStart());
      setTo(today);
    } else if (value === 'month') {
      const start = new Date();
      start.setDate(start.getDate() - 29);
      setFrom(toKey(start));
      setTo(today);
    }
  };

  const updates = useQuery({
    queryKey: ['dailyUpdates', from, to, person],
    queryFn: () => getDailyUpdates({ from, to, user_id: person || undefined })
  });
  const report = useQuery({
    queryKey: ['dailyUpdateReport', from, to, person],
    queryFn: () => getDailyUpdateReport({ from, to, user_id: person || undefined })
  });
  // The person list comes from the unfiltered report, so picking somebody does
  // not shrink the list to just them.
  const everyone = useQuery({
    queryKey: ['dailyUpdateReport', from, to, ''],
    queryFn: () => getDailyUpdateReport({ from, to })
  });

  const rows = updates.data?.data ?? [];
  const reportRows = report.data?.data?.rows ?? [];
  const totals = report.data?.data?.totals ?? {};
  const people = everyone.data?.data?.rows ?? [];
  const days = useMemo(() => groupByDay(rows), [rows]);
  const missing = reportRows.filter((r) => r.worked_days > r.updates);
  const failure = updates.error || report.error;

  const exportCsv = () => {
    const blob = new Blob([updatesToCsv(rows, formatTime)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `daily-updates-${from}${from === to ? '' : `-to-${to}`}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Layout>
      <Header backArrow onBack={onBack} data={{ name: 'Daily updates' }} />

      <PageBody>
        <FilterBar
          end={
            <button
              type="button"
              onClick={exportCsv}
              disabled={!rows.length}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 disabled:opacity-50"
            >
              <Download size={14} /> Export CSV
            </button>
          }
        >
          <SegmentedControl value={range} onChange={pickRange} options={RANGES} ariaLabel="Date range" />
          {range === 'custom' && (
            <>
              <DateField label="From" value={from} onChange={setFrom} />
              <DateField label="To" value={to} onChange={setTo} />
            </>
          )}
          <select
            value={person}
            onChange={(e) => setPerson(e.target.value)}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
            aria-label="Person"
          >
            <option value="">Everyone</option>
            {people.map((p) => (
              <option key={p.user_id} value={p.user_id}>
                {p.name}
              </option>
            ))}
          </select>
        </FilterBar>

        {failure && (
          <Callout tone="danger" icon={<AlertTriangle size={14} />}>
            {failure?.response?.data?.message || 'Could not load daily updates.'}
          </Callout>
        )}

        <TileRow>
          <Tile variant="card" label="Updates posted" value={totals.updates ?? 0} icon={<FileText size={18} />} />
          <Tile
            variant="card"
            label="People posting"
            value={`${totals.posting ?? 0} of ${totals.people ?? 0}`}
            hint="of those who worked in this range"
            icon={<Users size={18} />}
          />
          <Tile
            variant="card"
            label="Missing an update"
            value={missing.length}
            tone={missing.length ? 'warning' : undefined}
            hint={missing.length ? 'worked a day without posting' : undefined}
            icon={<UserX size={18} />}
            onClick={missing.length ? () => setView('report') : undefined}
          />
          <Tile
            variant="card"
            label="Blockers flagged"
            value={totals.blockers ?? 0}
            tone={totals.blockers ? 'warning' : undefined}
            icon={<AlertTriangle size={18} />}
          />
        </TileRow>

        <SegmentedControl value={view} onChange={setView} options={VIEWS} ariaLabel="View" size="sm" />

        {view === 'updates' ? (
          updates.isFetching && !rows.length ? (
            <Loader />
          ) : days.length === 0 ? (
            <EmptyState
              title="No updates in this range."
              hint="Updates are optional — people post them from the app when they punch out, and they appear here."
            />
          ) : (
            <div className="space-y-6">
              {days.map((day) => (
                <section key={day.date}>
                  <h3 className="mb-2 text-sm font-semibold text-gray-700">
                    {formatDay(day.date)}
                    <span className="ml-2 font-normal text-gray-400">
                      {day.items.length} update{day.items.length === 1 ? '' : 's'}
                    </span>
                  </h3>
                  <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                    {day.items.map((u) => (
                      <DailyUpdateCard key={u.id} update={u} highlighted={u.id === focus} />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )
        ) : (
          <TableCard title="By person" action={<span className="text-xs text-gray-500">Click a row to read their updates</span>}>
            <Table>
              <THead>
                <tr>
                  <Th>Employee</Th>
                  <Th align="right">Days worked</Th>
                  <Th align="right">Updates</Th>
                  <Th>Posted on</Th>
                  <Th align="right">Blockers</Th>
                  <Th>Last update</Th>
                </tr>
              </THead>
              <TBody>
                {reportRows.length === 0 && <EmptyRow colSpan={6}>Nobody worked or posted in this range.</EmptyRow>}
                {reportRows.map((r) => (
                  <Tr
                    key={r.user_id}
                    onClick={() => {
                      setPerson(String(r.user_id));
                      setView('updates');
                    }}
                  >
                    <Td>{r.name}</Td>
                    <Td numeric align="right">{r.worked_days}</Td>
                    <Td numeric align="right">{r.updates}</Td>
                    <Td>
                      <RateBar rate={r.rate} />
                    </Td>
                    <Td numeric align="right">
                      {r.blockers ? <span className="text-warning-fg">{r.blockers}</span> : 0}
                    </Td>
                    <Td>{r.last_update ? formatDay(r.last_update) : '—'}</Td>
                  </Tr>
                ))}
              </TBody>
            </Table>
          </TableCard>
        )}
      </PageBody>
    </Layout>
  );
};

/** Updates as a share of days worked; a bar reads faster than a column of percentages. */
const RateBar = ({ rate }) => {
  if (rate == null) return <span className="text-xs text-gray-400">no days worked</span>;
  const tone = rate >= 80 ? 'bg-success-fg' : rate >= 40 ? 'bg-warning-fg' : 'bg-danger-fg';
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-24 overflow-hidden rounded-full bg-gray-100">
        <div className={`h-full ${tone}`} style={{ width: `${rate}%` }} />
      </div>
      <span className="text-xs tabular-nums text-gray-600">{rate}%</span>
    </div>
  );
};

const DateField = ({ label, value, onChange }) => (
  <label className="flex items-center gap-2 text-xs font-medium text-gray-600">
    {label}
    <input
      type="date"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
    />
  </label>
);

export default DailyUpdates;
